import { useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import MantineTable from '../../../../../common/table/mantine';
import { CustomTextArea } from '../../../../../common/inputs/CustomTextArea';
import { Context } from '../context';
import { Button, Space } from '@mantine/core';
import { AppLoader } from '../../../../../components/common/loader';
import { CostingRequestsContext } from '../../../../../contexts/CostingRequest';
import { CostValueTemp, CostValueTempAlt } from '../helper';
import { useParams } from 'react-router-dom';
import { RefBadge } from '../../normsCheck/refBadge';
import { CrStatusBanner } from '../../projectOverview';

export const Table = ({ data = [], loading, rightButtons, activePlant }) => {
  const [initialState, setInitialState] = useState([]);
  const { crStepStatus } = useContext(CostingRequestsContext);
  const { form, onFormChange, onFormSubmit, readOnly, activePage, vicApprovalStatus } =
    useContext(Context);
  const [saveLoader, setSaveLoader] = useState({});
  const commentKey = activePage === 'vicApproval' ? 'vic_comment' : 'comment';
  const { crNumber } = useParams();
  useEffect(() => {
    setInitialState(data);
  }, [data]);

  const isInProgress = (cost) => {
    const pageKey = activePage === 'vicSubmission' ? 'VIC Submission' : 'VIC Approval';
    const isVicClosed =
      (crStepStatus?.[activePlant.producing_plant]?.[pageKey] || '').toLowerCase() === 'closed';
    return (cost.status || '').toLowerCase() === 'in progress' && !isVicClosed;
  };

  const handleSubmit = async (id) => {
    try {
      setSaveLoader({ ...saveLoader, [id]: true });
      await onFormSubmit(form[id], id, 'bom');
      updateInitialData(form[id], id);
    } catch (e) {
      console.log(e);
    } finally {
      setSaveLoader({ ...saveLoader, [id]: false });
    }
  };

  const updateInitialData = (info, id) => {
    const index = initialState.findIndex((item) => item.component_id === id);
    if (index > -1) {
      const updatedValues = [...initialState];
      updatedValues[index] = {
        ...updatedValues[index],
        [commentKey]: info?.[commentKey] || '',
      };
      setInitialState(updatedValues);
    }
  };

  const columns = useMemo(
    () =>
      [
        {
          accessorKey: 'material_group_description',
          header: 'Mat Group Desc',
        },
        {
          accessorKey: 'component_description',
          header: 'BOM/Norm Component',
        },
        {
          accessorKey: 'component',
          header: 'Resource',
        },
        {
          header: 'Total Value (per HL)',
          accessorKey: 'total_value_excel',
          size: 250,
          Cell: ({ row }) => {
            if (!row.original.component_id) {
              return (
                <CostValueTempAlt
                  main={row.original.RequestedSkuCostEstimation}
                  refData={row.original.PrimaryRefSkuCostEstimation}
                  field='total_value'
                />
              );
            } else {
              return <CostValueTemp cost={row.original.total_value_final} />;
            }
          },
        },

        {
          accessorKey: 'currency',
          header: 'Curr',
          Cell: ({ row }) => {
            if (!row.original.component_id) {
              return row.original.RequestedSkuCostEstimation.currency;
            } else {
              return row.original.currency;
            }
          },
        },
        {
          header: 'Qty',
          accessorKey: 'quantity_excel',
          size: 250,
          Cell: ({ row }) => {
            if (!row.original.component_id) {
              return (
                <CostValueTempAlt
                  main={row.original.RequestedSkuCostEstimation}
                  refData={row.original.PrimaryRefSkuCostEstimation}
                  field='quantity'
                />
              );
            } else {
              return <CostValueTemp cost={row.original.quantity_final} />;
            }
          },
        },

        {
          accessorKey: 'component_unit',
          header: 'Qty Unit',
          Cell: ({ row }) => {
            if (!row.original.component_id) {
              return row.original.RequestedSkuCostEstimation.uom;
            } else {
              return row.original.component_unit;
            }
          },
        },

        {
          header: 'Price',
          accessorKey: 'price_value_excel',
          size: 250,
          Cell: ({ row }) => {
            if (!row.original.component_id) {
              return (
                <CostValueTempAlt
                  main={row.original.RequestedSkuCostEstimation}
                  refData={row.original.PrimaryRefSkuCostEstimation}
                  field='price'
                />
              );
            } else {
              return <CostValueTemp cost={row.original.price_final} />;
            }
          },
        },

        {
          accessorKey: 'un',
          header: 'Price Unit',
          Cell: ({ row }) => {
            if (!row.original.component_id) {
              return row.original.RequestedSkuCostEstimation.un;
            } else {
              return row.original.un;
            }
          },
        },

        {
          header: 'Price Per Unit',
          accessorKey: 'price_unit_excel',
          size: 250,
          Cell: ({ row }) => {
            if (!row.original.component_id) {
              return (
                <CostValueTempAlt
                  main={row.original.RequestedSkuCostEstimation}
                  refData={row.original.PrimaryRefSkuCostEstimation}
                  field='price_unit'
                />
              );
            } else {
              return <CostValueTemp cost={row.original.price_unit_final} />;
            }
          },
        },

        {
          header: 'Scrap(%)',
          accessorKey: 'scrap_excel',
          size: 250,
          Cell: ({ row }) => {
            if (!row.original.component_id) {
              return (
                <CostValueTempAlt
                  main={row.original.RequestedSkuCostEstimation}
                  refData={row.original.PrimaryRefSkuCostEstimation}
                  field='scrap'
                />
              );
            } else {
              return <CostValueTemp cost={row.original.scrap_final} />;
            }
          },
        },

        activePage === 'vicApproval' && {
          header: 'Comment (Submitted)',
          accessorKey: 'comment',
          Cell: ({ row }) => {
            if (row.original.isTotal) return '';
            return (
              <div>
                <div style={{ alignItems: 'center', marginBottom: '8px' }}>
                  <p key={row.original?.component}>{row?.original?.comment}</p>
                </div>
              </div>
            );
          },
        },

        {
          header: 'Comment',
          accessorKey: commentKey,
          Cell: ({ row }) => {
            const { component_id, read_only_bom } = row.original;
            return (
              <div>
                <div style={{ alignItems: 'center', marginBottom: '8px' }}>
                  <CustomTextArea
                    value={
                      form[component_id]
                        ? form[component_id]?.[commentKey]
                        : row.original?.[commentKey] || ''
                    }
                    placeholder='Comment here'
                    onChange={(value) => onFormChange(value, component_id, commentKey)}
                    disabled={!isInProgress(row.original) || readOnly || read_only_bom}
                  />
                </div>
              </div>
            );
          },
        },
        {
          id: 'action',
          header: 'Action',
          Cell: ({ row }) => {
            const { component_id, read_only_bom } = row.original;
            if (!row.original.component) {
              return '';
            } else
              return (
                <Button
                  disabled={
                    !form[component_id] ||
                    saveLoader[component_id] ||
                    !isInProgress(row.original) ||
                    readOnly ||
                    read_only_bom
                  }
                  color='dark'
                  fullWidth
                  size='sm'
                  radius='sm'
                  className='add-supplier-button'
                  onClick={() => handleSubmit(component_id)}
                >
                  {saveLoader[component_id] ? <AppLoader size='sm' /> : 'Submit'}
                </Button>
              );
          },
        },
      ].filter((column) => column),
    [form, saveLoader, readOnly, crStepStatus],
  );

  const getExportData = () => {
    const dataToExport = [];

    // Helper to process each row
    const processRow = (data) => {
      dataToExport.push({
        'BOM/Norm Component': data.component_description || '',
        Resource: data.component || '',
        'Total Value (per HL) Main': data?.total_value_final?.[0]?.split(':')[1]?.trim() || '',
        'Total Value (per HL) Ref': data?.total_value_final?.[1]?.split(':')[1]?.trim() || '',
        'Total Value (per HL) Var': data?.total_value_final?.[2]?.split(':')[1]?.trim() || '',
        Currency: data.currency || '',
        'Quantity Main': data?.quantity_final?.[0]?.split(':')[1]?.trim() || '',
        'Quantity Ref': data?.quantity_final?.[1]?.split(':')[1]?.trim() || '',
        'Quantity Var': data?.quantity_final?.[2]?.split(':')[1]?.trim() || '',
        'Qty Unit': data.component_unit || '',
        'Price Main': data?.price_final?.[0]?.split(':')[1]?.trim() || '',
        'Price Ref': data?.price_final?.[1]?.split(':')[1]?.trim() || '',
        'Price Var': data?.price_final?.[2]?.split(':')[1]?.trim() || '',
        'Price Unit': data.un || '',
        'Price Per Unit Main': data?.price_unit_final?.[0]?.split(':')[1]?.trim() || '',
        'Price Per Unit Ref': data?.price_unit_final?.[1]?.split(':')[1]?.trim() || '',
        'Price Per Unit Var': data?.price_unit_final?.[2]?.split(':')[1]?.trim() || '',
        'Scrap Main': data?.scrap_final?.[0]?.split(':')[1]?.trim() || '',
        'Scrap Ref': data?.scrap_final?.[1]?.split(':')[1]?.trim() || '',
        'Scrap Var': data?.scrap_final?.[2]?.split(':')[1]?.trim() || '',
        Comment: data.comment || '',
      });
    };

    // Iterate through parent rows and pick only subRows or standalone items
    initialState.forEach((item) => {
      if (Array.isArray(item.subRows) && item.subRows.length > 0) {
        // Export subRows only
        item.subRows.forEach(processRow);
      } else {
        // Export normal items
        processRow(item);
      }
    });

    return dataToExport;
  };

  return (
    <>
      {['Approved', 'Rejected'].includes(vicApprovalStatus || '') &&
        activePage === 'vicApproval' && (
          <div>
            <Space h='md' />
            <CrStatusBanner>{`The VIC approval has been ${vicApprovalStatus.toLowerCase()}.`}</CrStatusBanner>
            <Space h='md' />
          </div>
        )}

      <MantineTable
        columns={columns}
        initialData={initialState}
        enablePinning
        initialState={{
          sorting: [
            {
              id: 'component',
              desc: true,
            },
          ],
          columnPinning: { left: ['mrt-row-expand'] }
        }}
        unique='indexNo'
        enableRowSelection={false}
        loading={loading}
        showSelectedToggle={false}
        hideSelectColumn
        showResetAll={false}
        enableExport
        getExportData={getExportData}
        enableExpanding
        positionExpandColumn = {'first'}
        enablePagination={false}
        enableBottomToolbar={false}
        enableSelectAll={false}
        enableColumnFilter={false}
        enableFilters={false}
        columnFilterDisplayMode={false}
        rightButtons={rightButtons}
        headerBadge={
          <RefBadge
            crNumber={crNumber}
            activePlant={activePlant?.producing_plant}
            dualReferenceSku={true}
          />
        }
        csvFilename={`vic_${
          activePage === 'vicApproval' ? 'approval' : 'submission'
        }_bom_${moment().format('YYYY-MM-DD')}`}
        mantineTableProps={{
          style: { fontSize: '14px' },
          highlightOnHover: false,
          withBorder: true,
          withColumnBorders: true,
        }}
        mantineTableHeadCellProps={{
          style: {
            textAlign: 'center',
            padding: '4px 6px',
            fontSize: '14px',
          },
        }}
        mantineTableBodyCellProps={{
          style: {
            padding: '2px 6px',
            fontSize: '14px',
          },
        }}
        mantineTableBodyRowProps={{
          style: { height: '24px' },
        }}
        mantinePaperProps={{
          style: { padding: 0, margin: 0, boxShadow: 'none' },
        }}
      />
    </>
  );
};
